import { CSSResultGroup, LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './ghs-mychart-options.css.lit';
import './mychart-options-menu';

type MenuMode = 'popover' | 'accordion';

@customElement('mychart-options')
export class MyChartOptions extends LitElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, attribute: 'button-variant', reflect: true })
  buttonVariant = 'primary';

  @property({ type: Number, attribute: 'menu-breakpoint', reflect: true })
  menuBreakpoint?: number;

  @property({ type: String, attribute: 'menu-mode', reflect: true })
  narrowScreenMenuMode: MenuMode = 'accordion';

  @property({ type: String, attribute: 'bellin-url', reflect: true })
  bellinUrl = '';
  @property({ type: String, attribute: 'bellin-button-text', reflect: true })
  bellinButtonText = '';
  @property({ type: String, attribute: 'bellin-paragraph', reflect: true })
  bellinParagraph = '';
  @property({ type: String, attribute: 'emplify-url', reflect: true })
  emplifyUrl = '';
  @property({ type: String, attribute: 'emplify-button-text', reflect: true })
  emplifyButtonText = '';
  @property({ type: String, attribute: 'emplify-paragraph', reflect: true })
  emplifyParagraph = '';
  @property({ type: String, attribute: 'extra-link-url', reflect: true })
  extraLinkUrl = '';
  @property({ type: String, attribute: 'extra-link', reflect: true })
  extraLink = '';
  @property({ type: String, attribute: 'button-text', reflect: true })
  buttonText = '';

  @state() private menuOpen = false;

  private mediaQueryList?: MediaQueryList;
  private hoverTimeout: number | undefined;
  private abortController = new AbortController();
  private resizeObserver = new ResizeObserver(() => this.adjustMenuPosition());
  private resizeTimeout: number | undefined;

  get menuMode(): MenuMode {
    if (!this.menuBreakpoint || !this.mediaQueryList)
      return this.narrowScreenMenuMode;
    const isWide = this.mediaQueryList.matches;
    return isWide
      ? this.narrowScreenMenuMode === 'accordion'
        ? 'popover'
        : 'accordion'
      : this.narrowScreenMenuMode;
  }

  connectedCallback() {
    super.connectedCallback();

    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) {
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
    }

    if (this.menuBreakpoint !== undefined) {
      const query = `(min-width: ${this.menuBreakpoint}px)`;
      this.mediaQueryList = window.matchMedia(query);
      this.mediaQueryList.addEventListener('change', this.handleMediaChange, {
        signal: this.abortController.signal,
      });
    }

    window.addEventListener('resize', this.handleResize);

    document.addEventListener('keydown', this.handleKeyDown, {
      signal: this.abortController.signal,
    });
    document.addEventListener('click', this.handleOutsideClick, {
      signal: this.abortController.signal,
    });
  }

  disconnectedCallback() {
    this.abortController.abort();
    clearTimeout(this.hoverTimeout);

    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) {
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
    }

    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);

    this.resizeObserver.disconnect();

    super.disconnectedCallback();
  }

  firstUpdated() {
    const menu = this.renderRoot.querySelector('.menu-wrapper') as HTMLElement;
    if (menu) {
      this.resizeObserver.observe(menu);
      this.adjustMenuPosition();
    }
  }

  updated() {
    this.adjustMenuPosition();
  }

  private adjustMenuPosition = () => {
    requestAnimationFrame(() => {
      const menu = this.renderRoot.querySelector(
        '.menu-wrapper'
      ) as HTMLElement | null;
      const accordion = this.renderRoot.querySelector(
        '.accordion-menu'
      ) as HTMLElement | null;
      const button = this.renderRoot.querySelector('ghs-button') as HTMLElement;

      const isAccordion = this.menuMode === 'accordion';
      const isPopover = this.menuMode === 'popover';

      if (isAccordion && accordion) {
        // Reset styles for accordion
        Object.assign(accordion.style, {
          position: '',
          left: '',
          right: '',
          transform: '',
        });
        return;
      }

      if (!isPopover || !menu) return;

      // Default popover positioning
      Object.assign(menu.style, {
        position: 'absolute',
        left: '50%',
        right: 'auto',
        transform: 'translateX(-50%)',
      });

      const menuRect = menu.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();

      const overflowLeft = Math.max(0, 0 - menuRect.left);
      const overflowRight = Math.max(0, menuRect.right - window.innerWidth);

      if (overflowLeft > 0 || overflowRight > 0) {
        const alignLeft =
          buttonRect.left < window.innerWidth - buttonRect.right;
        const offsetParent = menu.offsetParent as HTMLElement | null;
        const offsetParentRect = offsetParent?.getBoundingClientRect() ?? {
          left: 0,
          width: 0,
        };
        const scrollX = window.scrollX;

        if (alignLeft) {
          const leftOffset = -offsetParentRect.left + scrollX;
          Object.assign(menu.style, {
            left: `${leftOffset}px`,
            right: 'auto',
          });
        } else {
          const rightOffset =
            -(
              window.innerWidth -
              offsetParentRect.left -
              (offsetParent?.offsetWidth ?? 0)
            ) + scrollX;
          Object.assign(menu.style, {
            right: `${rightOffset}px`,
            left: 'auto',
          });
        }

        menu.style.transform = 'translateX(0)';
      }
    });
  };

  private handleMediaChange = () => {
    this.adjustMenuPosition();
  };

  private handleMouseEnter = () => {
    clearTimeout(this.hoverTimeout);
    this.menuOpen = true;
  };

  private handleMouseLeave = () => {
    this.hoverTimeout = window.setTimeout(() => {
      this.closeMenu();
    }, 200);
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.closeMenu();
    }
  };

  private handleOutsideClick = (event: MouseEvent) => {
    const path = event.composedPath();
    if (!path.includes(this)) {
      this.closeMenu();
    }
  };

  private toggleMenu = () => {
    this.menuOpen = !this.menuOpen;
  };

  private closeMenu() {
    if (!this.menuOpen) return;

    const menu = this.renderRoot.querySelector(
      '.menu-wrapper, .accordion-menu'
    );
    const activeElement = this.shadowRoot?.activeElement ?? null;

    this.menuOpen = false;

    if (menu?.contains(activeElement)) {
      const ghsButton = this.renderRoot.querySelector('ghs-button');
      const shadowButton = ghsButton?.shadowRoot?.querySelector('button');

      if (shadowButton instanceof HTMLElement) {
        shadowButton.focus();
      } else if (ghsButton instanceof HTMLElement) {
        ghsButton.focus();
      }
    }
  }

  private handleResize = () => {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(() => {
      this.adjustMenuPosition();
    }, 100);
  };

  render() {
    const isAccordion = this.menuMode === 'accordion';
    const menuClass = isAccordion
      ? `accordion-menu${this.menuOpen ? ' open' : ''}`
      : `menu-wrapper${this.menuOpen ? ' open' : ' hidden'}`;

    return html`
      <div class="container">
        <ghs-button
          @click=${this.toggleMenu}
          container-fit-variant=""
          icon=${this.menuOpen ? 'chevron-up' : 'chevron-down'}
          icon-position="right"
          site="BH"
          variant="${this.buttonVariant}"
          ?border-bottom-radius=${this.menuOpen}
        >
          <ghs-icon
            name="heart-folder"
            mobile-size="14px"
            size="20px"
            site="BH"
            class="heart-health"
          ></ghs-icon>
          ${this.buttonText}
        </ghs-button>

        <div class="${menuClass}">
          <mychart-options-menu
            .bellinUrl=${this.bellinUrl}
            .bellinButtonText=${this.bellinButtonText}
            .bellinParagraph=${this.bellinParagraph}
            .emplifyUrl=${this.emplifyUrl}
            .emplifyButtonText=${this.emplifyButtonText}
            .emplifyParagraph=${this.emplifyParagraph}
            .extraLinkUrl=${this.extraLinkUrl}
            .extraLink=${this.extraLink}
          ></mychart-options-menu>
        </div>
      </div>
    `;
  }
}
